import {
  Button,
  Card,
  CardBody,
  CardHeader,
  type ColumnDef,
  Combobox,
  DataTable,
  EmptyState,
  Field,
  type FilterOption,
  FormGrid,
  FormGridItem,
  InlineMessage,
  Input,
  OptionSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RecordFormDialog,
  type RecordTab,
  Spinner,
  Stack,
  StatTile,
} from '@olytic/foundation';
import { QuoteDiscountBar, QuoteLines, quoteLineGroupKeys } from '@olytic/foundation/cpq';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { formatCurrency, formatDate, humanize } from '../objects/format';
import { recordPath } from '../objects/routes';
import {
  addLine,
  type BuilderLine,
  type BundleOption,
  type SellingModel,
  type BuilderQuote,
  type BuilderTotals,
  type CatalogProduct,
  type DiscountType,
  type PriceGrid,
  type PriceOption,
  fetchBuilder,
  fetchCatalog,
  generateSchedules,
  type QuoteBuilder,
  duplicateQuote,
  makePrimary,
  removeLine,
  saveDocuments,
  saveRamp,
  savePolicy,
  saveLine,
  savePricing,
} from './quotesApi';
import { DateSetting, NumberSetting, Total } from './fields';
import { bundleIds, describeTiers, orderLines } from './lineTree';
import { catalogPrice, cellOptions, gridOf, priceChoices, quantityText, sizeKeyOf } from './prices';
import { RevenueScheduleCard } from './RevenueScheduleCard';
import { CatalogTab } from './CatalogTab';
import { effectText, orderSections, orderStats } from './orderView';
import { OptionsTab } from './OptionsTab';
import { RampDialog } from './RampDialog';
import { PricingTab } from './PricingTab';
import { type BusinessCase, readCase } from './businessCase';
import { type ProposalLayout, readLayout } from './proposal';
import { ProposalTab } from './ProposalTab';
import { RoiTab } from './RoiTab';
import type { Terms } from '@quote-math/terms';
import { deriveWorkspace } from '@quote-math/workspace';
import { TermsTab } from './TermsTab';
import type { ValueModel } from '@quote-math/valueModel';
import { ValueTab } from './ValueTab';
import { ScopeTab } from './ScopeTab';

/** A quote's workspace tabs, in order; they follow Details on its record page. */
export const QUOTE_TABS = [
  { key: 'scope', label: 'Scope' },
  { key: 'catalog', label: 'Catalog' },
  { key: 'order', label: 'Order' },
  { key: 'options', label: 'Options' },
  { key: 'value', label: 'Value' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'roi', label: 'ROI' },
  { key: 'terms', label: 'Terms' },
  { key: 'proposal', label: 'Proposal' },
] as const;
export const QUOTE_TAB_KEYS: readonly string[] = QUOTE_TABS.map((t) => t.key);

const NONE = '';
const DISCOUNT_OPTIONS: FilterOption[] = [
  { value: 'percent', label: 'Percent off' },
  { value: 'amount', label: 'Amount off' },
  { value: 'target', label: 'Target price' },
];
const INCLUSION_OPTIONS: FilterOption[] = [
  { value: 'workstream', label: 'Workstream' },
  { value: 'complimentary', label: 'Complimentary' },
];
const money = (value: number) => formatCurrency(value);
/** Line fields saved as text (or null), not numbers. */
const TEXT_KEYS = new Set(['inclusion', 'start_date', 'end_date', 'price_id', 'price_grid_cell_id', 'billing_treatment_id']);

/**
 * The quote workspace, the proposal builder's tabs over one quote, as tabs for
 * its record page: Scope (what we'll build), Lines (products, prices and
 * totals; pricing.mjs), Options (sibling quotes side by side), Value, Pricing,
 * ROI, Terms and Proposal. Loads only when `enabled` (a quote's page);
 * `onPriced` hears the totals after every change, so Details can show them.
 */
export function useQuoteWorkspace(
  id: string,
  enabled: boolean,
  { onPriced }: { onPriced?: (totals: BuilderTotals) => void } = {},
): { tabs: RecordTab[]; refresh: () => void } {
  const navigate = useNavigate();
  const [data, setData] = useState<QuoteBuilder | null>(null);
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);
  const [bundleOptions, setBundleOptions] = useState<BundleOption[]>([]);
  const [sellingModels, setSellingModels] = useState<SellingModel[]>([]);
  const [priceOptions, setPriceOptions] = useState<PriceOption[]>([]);
  const [priceGrids, setPriceGrids] = useState<PriceGrid[]>([]);
  // The bundle line whose Add option dialog is open.
  const [addingTo, setAddingTo] = useState<BuilderLine | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Bumped on every pick, so the product box starts empty again.
  const [pickerKey, setPickerKey] = useState(0);
  // The record page passes a new callback every render; the latest is the one to call.
  const onPricedRef = useRef(onPriced);
  onPricedRef.current = onPriced;
  const [loads, setLoads] = useState(0);
  // The Order tab: collapsed groups, descriptions shown, and the detail table.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [hideDescriptions, setHideDescriptions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // The term line whose ramp is being edited.
  const [ramping, setRamping] = useState<BuilderLine | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    Promise.all([fetchBuilder(authenticatedFetch, id), fetchCatalog(authenticatedFetch)])
      .then(([builder, found]) => {
        if (!live) return;
        setData(builder);
        setCatalog(found.products);
        setBundleOptions(found.bundleOptions);
        setSellingModels(found.sellingModels);
        setPriceOptions(found.priceOptions);
        setPriceGrids(found.priceGrids);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the quote.'));
    return () => {
      live = false;
    };
  }, [enabled, id, loads]);

  /** Runs a write; the answer is the repriced quote. */
  const run = useCallback(async (work: () => Promise<QuoteBuilder>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      setData(next);
      onPricedRef.current?.(next.totals);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }, []);

  const pricing = (body: Partial<BuilderQuote>) => run(() => savePricing(authenticatedFetch, id, body));

  const loadProducts = useCallback(
    async (query: string): Promise<FilterOption[]> => {
      const q = query.trim().toLowerCase();
      return catalog
        .filter((p) => !q || `${p.code} ${p.name} ${p.group_name}`.toLowerCase().includes(q))
        .slice(0, 50)
        .map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }));
    },
    [catalog],
  );

  const bundles = useMemo(() => bundleIds(data?.lines ?? []), [data]);
  const isBundle = useCallback((row: BuilderLine) => bundles.has(row.id) || row.structure === 'bundle', [bundles]);
  const columns = useMemo<ColumnDef<BuilderLine>[]>(
    () => [
      {
        key: 'name', label: 'Item', width: 280, get: (row) => row.name,
        // Options sit indented under their bundle; a bundle says how it's priced.
        render: (row) =>
          row.parent_line_id ? (
            // Inline: the studio has only the library's own CSS classes.
            <span style={{ display: 'block', paddingLeft: '1.5rem' }}>{row.name}</span>
          ) : isBundle(row) ? (
            <span className="flex items-baseline gap-2">
              <span className="font-emphasis">{row.name}</span>
              <span className="text-xs text-[var(--fg-muted)]">{row.list_unit_price > 0 ? 'Bundle · fixed fee' : 'Bundle · rolls up'}</span>
            </span>
          ) : (
            row.name
          ),
      },
      { key: 'product_code', label: 'Code', nowrap: true, get: (row) => row.product_code ?? '' },
      {
        // A product with more than one price picks among them; otherwise it uses its default.
        key: 'price_id', label: 'Price', nowrap: true, get: (row) => row.price_label ?? row.selling_model ?? '—',
        editor: {
          kind: 'select',
          optionsFor: (row) => priceChoices(row.product_id, priceOptions, money),
          getValue: (row) => row.price_id ?? '',
        },
        editable: (row) => !row.parent_line_id && priceChoices(row.product_id, priceOptions, money).length > 1,
      },
      {
        // A Price Grid's menu: the cell the quote's Company Size (or the line's choice) picked.
        key: 'price_grid_cell_id', label: 'Option', nowrap: true,
        get: (row) => {
          const cell = row.price_grid_cell_snapshot;
          return cell ? (cell.label ?? cell.labels.join(' · ')) : row.price_scheme === 'grid' ? 'Choose' : '—';
        },
        editor: {
          kind: 'select',
          optionsFor: (row) => cellOptions(gridOf(row, priceOptions, priceGrids), data?.companySizes ?? [], money),
          getValue: (row) => row.price_grid_cell_id ?? '',
        },
        editable: (row) => row.price_scheme === 'grid' && !row.parent_line_id,
      },
      {
        key: 'selling_model_id', label: 'Sold as', nowrap: true, get: (row) => row.selling_model ?? humanize(row.billing),
        editor: {
          kind: 'select',
          options: sellingModels.map((m) => ({ value: String(m.id), label: m.name })),
          getValue: (row) => (row.selling_model_id === null ? '' : String(row.selling_model_id)),
        },
        // An option is sold as part of its bundle.
        editable: (row) => !row.parent_line_id,
      },
      {
        key: 'start_date', label: 'Starts', nowrap: true, get: (row) => (row.start_date ? formatDate(row.start_date) : '—'),
        editor: { kind: 'date', nullable: true, getValue: (row) => row.start_date ?? '' },
        editable: (row) => !row.parent_line_id,
      },
      {
        // A term line's length in whole months, from the quote's term unless set here; it ends a term after it starts.
        key: 'term_months', label: 'Term (Months)', align: 'right', nowrap: true, get: (row) => (row.term_months ? String(row.term_months) : '—'),
        editor: { kind: 'number', min: 1, max: 240, step: 1, getValue: (row) => (row.term_months ? String(row.term_months) : '') },
        editable: (row) => !row.parent_line_id && row.selling_model_kind === 'term_defined',
      },
      {
        key: 'end_date', label: 'Ends', nowrap: true, get: (row) => (row.end_date ? formatDate(row.end_date) : '—'),
        editor: { kind: 'date', nullable: true, getValue: (row) => row.end_date ?? '' },
        editable: (row) => !row.parent_line_id,
      },
      {
        key: 'quantity', label: 'Qty', align: 'right', nowrap: true, get: (row) => quantityText(row),
        // Steps count from the minimum, so a step of 1 from 0.01 would refuse 200.
        editor: { kind: 'number', min: 0.01, step: 0.01 },
      },
      {
        key: 'list_unit_price', label: 'List Price', align: 'right', nowrap: true, render: (row) => money(row.list_unit_price),
        editor: { kind: 'number', min: 0, step: 1, getValue: (row) => String(row.list_unit_price) },
      },
      {
        key: 'discount_pct', label: 'Discount %', align: 'right', nowrap: true, get: (row) => (row.discount_pct ? `${row.discount_pct}%` : '—'),
        editor: { kind: 'number', min: -100, max: 100, step: 1, getValue: (row) => String(row.discount_pct) },
      },
      {
        key: 'discount_months', label: 'For Months', align: 'right', nowrap: true, get: (row) => (row.discount_months ? String(row.discount_months) : '—'),
        editor: { kind: 'number', min: 1, max: 120, step: 1, nullable: true },
        editable: (row) => row.billing === 'monthly',
      },
      {
        key: 'inclusion', label: 'Included', nowrap: true, get: (row) => (row.inclusion ? humanize(row.inclusion) : '—'),
        editor: { kind: 'select', options: INCLUSION_OPTIONS, nullable: true },
      },
      {
        // The billing treatment: the product's policy default unless the line chose another.
        key: 'billing_treatment_id', label: 'Billing', nowrap: true,
        get: (row) => row.billing_treatment_snapshot?.name ?? '—',
        editor: {
          kind: 'select',
          nullable: true,
          options: (data?.billingTreatments ?? []).map((t) => ({ value: t.id, label: `${t.name} (${t.policy})` })),
          getValue: (row) => row.billing_treatment_id ?? '',
        },
        editable: (row) => !row.parent_line_id && row.product_id !== null,
      },
      {
        key: 'volumeDiscount', label: 'Volume', align: 'right', nowrap: true,
        render: (row) =>
          row.volumeDiscount ? (
            <span title={describeTiers(row.price_adjustment, money)}>{money(-row.volumeDiscount)}</span>
          ) : row.price_adjustment ? (
            <span title={describeTiers(row.price_adjustment, money)}>—</span>
          ) : (
            '—'
          ),
      },
      {
        key: 'net', label: 'Net', align: 'right', nowrap: true,
        render: (row) => (row.scope ? 'Scope' : row.included ? 'Included' : money(row.net)),
      },
      {
        // Over its life: one-time net, or a term line's monthly net × its term.
        key: 'total', label: 'Total', align: 'right', nowrap: true,
        render: (row) => (row.scope || row.included || row.total === null || row.total === undefined ? '—' : money(row.total)),
      },
    ],
    [isBundle, sellingModels, priceOptions, priceGrids, data],
  );

  const refresh = useCallback(() => setLoads((n) => n + 1), []);

  const panel = (body: JSX.Element) => (
    <Stack>
      {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
      {body}
    </Stack>
  );
  if (!enabled) return { tabs: [], refresh };
  if (!data) {
    const waiting = error ? <></> : <div className="flex justify-center py-8"><Spinner /></div>;
    return { tabs: QUOTE_TABS.map((t) => ({ key: t.key, label: t.label, content: panel(waiting) })), refresh };
  }

  const { quote, lines, totals, assets } = data;
  // The timeline, once it has phases, sets when benefits start.
  const { timeline, terms, valueModel, timelineGoLive } = deriveWorkspace(data);
  const saveTerms = (next: Terms) =>
    void run(() => saveDocuments(authenticatedFetch, id, { proposal: { ...(quote.proposal ?? {}), terms: next as unknown as Record<string, unknown> } }));
  const layout = readLayout(quote.proposal);
  const memo = readCase(quote.proposal);
  const saveLayout = (next: ProposalLayout) =>
    void run(() =>
      saveDocuments(authenticatedFetch, id, {
        proposal: { ...(quote.proposal ?? {}), sections: next.sections, custom: next.custom, investment: next.investment } as Record<string, unknown>,
      }),
    );
  const saveMemo = (next: BusinessCase) =>
    void run(() => saveDocuments(authenticatedFetch, id, { proposal: { ...(quote.proposal ?? {}), memo: next as unknown as Record<string, unknown> } }));
  const saveValue = (model: ValueModel) =>
    void run(() => saveDocuments(authenticatedFetch, id, { value_model: model as unknown as Record<string, unknown> }));
  const changeOrder = quote.kind === 'change_order';
  const assetOptions: FilterOption[] = assets.map((a) => ({ value: a.id, label: `${a.name} (${money(a.monthly)}/mo)` }));
  const sections = orderSections(lines, catalog, money);
  const stats = orderStats(totals, money);

  const content: Record<(typeof QUOTE_TABS)[number]['key'], JSX.Element> = {
    scope: (
        <ScopeTab
          data={data}
          catalog={catalog}
          busy={busy}
          onSave={(scope) => void run(() => saveDocuments(authenticatedFetch, id, { scope }))}
          onBuildOrder={(wanted) =>
            void run(async () => {
              // Studio lines follow the scope; consulting and Foundation lines stay as they are.
              let latest = data;
              const studio = lines.filter((l) => l.family === 'studio' && !l.parent_line_id && l.product_code);
              for (const line of studio) {
                if (!wanted.has(line.product_code!)) latest = await removeLine(authenticatedFetch, id, line.id);
              }
              for (const [code, quantity] of wanted) {
                const line = studio.find((l) => l.product_code === code);
                const product = catalog.find((p) => p.code === code);
                if (line && line.quantity !== quantity) latest = await saveLine(authenticatedFetch, id, line.id, { quantity });
                else if (!line && product) latest = await addLine(authenticatedFetch, id, product.id, { quantity });
              }
              return latest;
            })
          }
        />
          
    ),
    catalog: (
      <CatalogTab
        catalog={catalog}
        lines={lines}
        priceOf={(product) => catalogPrice(product, { options: priceOptions, grids: priceGrids, sizeKey: sizeKeyOf(quote) })}
        busy={busy}
        onQuantityChange={(product, quantity) => {
          const existing = lines.find((l) => l.product_id === product.id && !l.parent_line_id);
          if (existing && quantity === 0) void run(() => removeLine(authenticatedFetch, id, existing.id));
          else if (existing) void run(() => saveLine(authenticatedFetch, id, existing.id, { quantity }));
          else if (quantity > 0) void run(() => addLine(authenticatedFetch, id, product.id, { quantity }));
        }}
      />
    ),
    order: (
    <Stack>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-80">
          <Combobox
            key={pickerKey}
            aria-label="Add a product"
            loadOptions={loadProducts}
            placeholder="Add a product…"
            disabled={busy}
            onSelect={(option) => {
              setPickerKey((k) => k + 1);
              if (option) void run(() => addLine(authenticatedFetch, id, option.value));
            }}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="neutral" size="sm" onClick={() => setCollapsed(new Set())}>Expand all</Button>
          <Button variant="neutral" size="sm" onClick={() => setCollapsed(new Set(quoteLineGroupKeys(sections)))}>Collapse all</Button>
          <Button variant="neutral" size="sm" onClick={() => setHideDescriptions((h) => !h)}>{hideDescriptions ? 'Show descriptions' : 'Hide descriptions'}</Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="neutral" size="sm">Configure</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <Stack>
                <DateSetting
                  label="Start Date"
                  value={quote.start_date ?? null}
                  help="When its lines start, unless a line says otherwise."
                  onSave={(start_date) => void pricing({ start_date })}
                />
                <NumberSetting
                  label="Term (Months)"
                  value={quote.term_months ?? null}
                  help="Its term lines' length, unless a line says otherwise; blank: each selling model's default."
                  onSave={(term_months) => void pricing({ term_months: term_months === null ? null : Math.round(term_months) })}
                  disabled={busy}
                />
                <Field
                  label="Company Size"
                  help={quote.company_size_label ? `Prices from its Price Grid. The account is ${quote.company_size_label}.` : 'Prices from its Price Grid. The account has no Annual Revenue yet.'}
                >
                  <OptionSelect
                    options={(data.companySizes ?? []).map((s) => ({ value: s.key, label: s.label }))}
                    emptyLabel={quote.company_size_label ? `As the account (${quote.company_size_label})` : 'Not set'}
                    value={quote.grid_overrides?.['account.company_size'] ?? NONE}
                    onValueChange={(v) => {
                      const rest = Object.fromEntries(Object.entries(quote.grid_overrides ?? {}).filter(([key]) => key !== 'account.company_size'));
                      void pricing({ grid_overrides: v === NONE ? rest : { ...rest, 'account.company_size': v } });
                    }}
                    disabled={busy}
                  />
                </Field>
                {changeOrder && assetOptions.length ? (
                  <Field label="Replaces" help="A current subscription this quote replaces.">
                    <OptionSelect
                      options={assetOptions}
                      emptyLabel="Nothing"
                      value={lines.find((l) => l.replaces_asset_id)?.replaces_asset_id ?? NONE}
                      onValueChange={(v) => {
                        const target = lines.find((l) => l.billing === 'monthly') ?? lines[0];
                        if (target) void run(() => saveLine(authenticatedFetch, id, target.id, { replaces_asset_id: v === NONE ? null : v }));
                      }}
                      disabled={busy || lines.length === 0}
                    />
                  </Field>
                ) : null}
              </Stack>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid gap-2.5 md:grid-cols-3">
        <StatTile className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]" label="One-Time" value={stats.oneTime.value} note={stats.oneTime.note} />
        <StatTile className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]" label="Monthly" value={stats.monthly.value} note={stats.monthly.note} />
        <StatTile className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)]" label="Total Discount" value={stats.discount.value} note={stats.discount.note} />
      </div>

      <QuoteDiscountBar
        disabled={busy}
        rows={[
          {
            key: 'one',
            label: 'One-Time',
            list: `List ${money(totals.oneTime.list)}${totals.oneTime.included ? ' after included work' : ''}`,
            mode: quote.one_time_discount_type,
            value: quote.one_time_discount_value,
            effect: effectText(totals.oneTime.list, totals.oneTime.net, money),
          },
          {
            key: 'mon',
            label: 'Monthly',
            list: `List ${money(totals.monthly.list)} / mo`,
            mode: quote.monthly_discount_type,
            value: quote.monthly_discount_value,
            effect: effectText(totals.monthly.list, totals.monthly.net, money),
            extra: quote.monthly_discount_type ? (
              <span className="w-40">
                <NumberSetting label="For Months" value={quote.monthly_discount_months} disabled={busy} onSave={(v) => void pricing({ monthly_discount_months: v })} />
              </span>
            ) : null,
          },
        ]}
        onChange={(key, { mode, value }) =>
          void pricing(
            key === 'one'
              ? { one_time_discount_type: mode, one_time_discount_value: mode ? value : null }
              : { monthly_discount_type: mode, monthly_discount_value: mode ? value : null, ...(mode ? {} : { monthly_discount_months: null }) },
          )
        }
      />

      <QuoteLines
        sections={sections}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        hideDescription={hideDescriptions}
        empty="No lines yet. Add products here or set quantities on the Catalog tab."
        onRamp={(line) => setRamping(lines.find((l) => l.id === line.id) ?? null)}
        onRemove={(line) => void run(() => removeLine(authenticatedFetch, id, line.id))}
      />
      <RampDialog
        line={ramping}
        options={ramping ? cellOptions(gridOf(ramping, priceOptions, priceGrids), data.companySizes ?? [], money) : []}
        busy={busy}
        onClose={() => setRamping(null)}
        onSave={(body) => {
          const line = ramping;
          if (!line) return;
          void run(() => saveRamp(authenticatedFetch, id, line.id, body)).then(() => setRamping(null));
        }}
      />

      <Card>
        <CardHeader
          title="Line details"
          actions={
            <Button variant="subtle" size="sm" onClick={() => setShowDetails((v) => !v)}>
              {showDetails ? 'Hide' : 'Show'}
            </Button>
          }
        />
        {showDetails && lines.length ? (
          <DataTable<BuilderLine>
            columns={columns}
            rows={orderLines(lines)}
            getRowId={(row) => row.id}
            density="compact"
            onCellSave={(row, key, value) =>
              run(() =>
                saveLine(authenticatedFetch, id, row.id, {
                  // Dates, the inclusion and ids stay text; everything else is a number.
                  [key]: TEXT_KEYS.has(key) ? value || null : value === null || value === '' ? null : Number(value),
                }),
              )
            }
            rowActions={[
              { label: 'Add option', onSelect: (row) => setAddingTo(row), hidden: (row) => !isBundle(row) || row.product_id === null },
              { label: 'Remove', destructive: true, onSelect: (row) => void run(() => removeLine(authenticatedFetch, id, row.id)) },
            ]}
          />
        ) : null}
      </Card>
      <RecordFormDialog
        open={addingTo !== null}
        onOpenChange={(open) => { if (!open) setAddingTo(null); }}
        title={addingTo ? `Add an option to ${addingTo.name}` : ''}
        submitLabel="Add"
        fields={[
          {
            key: 'option', label: 'Option', required: true,
            editor: {
              kind: 'select',
              options: bundleOptions
                .filter((o) => o.bundle_product_id === addingTo?.product_id)
                .map((o) => {
                  const product = catalog.find((p) => p.id === o.option_product_id);
                  const name = product ? `${product.code} · ${product.name}` : o.option_product_id;
                  return { value: o.option_product_id, label: o.option_group ? `${o.option_group}: ${name}` : name };
                }),
            },
          },
          { key: 'quantity', label: 'Quantity', editor: { kind: 'number', min: 0.01, step: 0.01 } },
        ]}
        onSubmit={async (values) => {
          const parent = addingTo;
          if (!parent || !values.option) return;
          await run(() => addLine(authenticatedFetch, id, values.option as string, { parentLineId: parent.id, ...(values.quantity ? { quantity: Number(values.quantity) } : {}) }));
          setAddingTo(null);
        }}
      />

      <RevenueScheduleCard
        lines={lines}
        rows={data.schedules ?? []}
        busy={busy}
        onGenerate={() => void run(() => generateSchedules(authenticatedFetch, id))}
      />
    </Stack>
    ),
    options: (
        <OptionsTab
          data={data}
          busy={busy}
          onDuplicate={() =>
            void (async () => {
              setBusy(true);
              try {
                const copy = await duplicateQuote(authenticatedFetch, id);
                navigate(recordPath('quotes', copy));
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Could not copy the quote.');
              } finally {
                setBusy(false);
              }
            })()
          }
          onMakePrimary={(optionId) =>
            void run(async () => {
              await makePrimary(authenticatedFetch, optionId);
              return fetchBuilder(authenticatedFetch, id);
            })
          }
        />
          
    ),
    value: (
        <ValueTab model={valueModel} onSave={saveValue} goLiveFromTimeline={timelineGoLive !== null} />
          
    ),
    pricing: (
        <PricingTab
          data={data}
          model={valueModel}
          busy={busy}
          onApply={(oneTime, notes) =>
            void run(async () => {
              await saveDocuments(authenticatedFetch, id, {
                price_recommendation: { oneTime, notes, appliedAt: new Date().toISOString() },
              });
              const atList = Math.abs(oneTime - totals.oneTime.list) < 1;
              return savePricing(authenticatedFetch, id, atList
                ? { one_time_discount_type: null, one_time_discount_value: null }
                : { one_time_discount_type: 'target', one_time_discount_value: oneTime });
            })
          }
          onSavePolicy={(patch) =>
            void run(async () => {
              await savePolicy(authenticatedFetch, patch);
              return fetchBuilder(authenticatedFetch, id);
            })
          }
        />
          
    ),
    roi: (
        <RoiTab data={data} model={valueModel} onSave={saveValue} />
          
    ),
    terms: (
        <TermsTab
          timeline={timeline}
          terms={terms}
          oneTime={totals.oneTime.net}
          model={valueModel}
          onSaveTimeline={(next) => void run(() => saveDocuments(authenticatedFetch, id, { timeline: next as unknown as Record<string, unknown> }))}
          onSaveTerms={saveTerms}
          onSaveModel={saveValue}
          memo={memo}
          onSaveMemo={saveMemo}
          layout={layout}
          onSaveLayout={saveLayout}
        />
          
    ),
    proposal: (
        <ProposalTab data={data} layout={layout} memo={memo} terms={terms} timeline={timeline} valueModel={valueModel} onSave={saveLayout} />
          
    ),
  };
  return {
    tabs: QUOTE_TABS.map((t) => ({
      key: t.key,
      label: t.label,
      content: panel(content[t.key]),
    })),
    refresh,
  };
}
